import React, { useEffect, useState } from 'react';
import { Alert, Button, Drawer, Form, Input, List, Modal, Space, Tag, Typography } from 'antd';
import {
    getGeiInventoryVersionsAPI,
    postCloseGeiVersionAPI,
    postCreateCorrectiveGeiVersionAPI,
    postCreateNextGeiVersionAPI
} from 'src/services/restcall';
import { openNotification } from 'src/globals/Components/Notification/Notificaction';
import { GeiInventoryVersion } from 'src/geiVersion/types';
import {
    formatVersionEventLine,
    formatVersionLabel,
    formatVersionStateBadge,
    isGeiLifecycleAllowed,
    lifecycleActionVisibility,
    lifecycleErrorCode,
    lifecycleErrorMessage,
    lifecycleErrorShouldRefresh,
    sortVersionsByCreatedAtDesc,
    suggestedNextLabel
} from 'src/geiVersion/display';

const { Text } = Typography;

interface VersionHistoryDrawerProps {
    open: boolean;
    geiInventoryId: string | null;
    groupId: string | null | undefined;
    onClose: () => void;
    onViewVersion: (versionId: string | null) => void;
}

function formatWhen(value: string | null | undefined): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
}

const VersionHistoryDrawer: React.FC<VersionHistoryDrawerProps> = (props) => {
    const [versions, setVersions] = useState<GeiInventoryVersion[]>([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [closeTarget, setCloseTarget] = useState<GeiInventoryVersion | null>(null);
    const [nextTarget, setNextTarget] = useState<GeiInventoryVersion | null>(null);
    const [correctiveTarget, setCorrectiveTarget] = useState<GeiInventoryVersion | null>(null);
    const [nextLabel, setNextLabel] = useState('');
    const [correctiveLabel, setCorrectiveLabel] = useState('');
    const [correctionReason, setCorrectionReason] = useState('');
    const [reasonError, setReasonError] = useState('');

    const canManage = isGeiLifecycleAllowed(props.groupId);
    const openExists = versions.some((version) => version.state === 'OPEN');

    const load = async (inventoryId: string) => {
        setLoading(true);
        const response = await getGeiInventoryVersionsAPI(inventoryId);
        setLoading(false);
        if (response.Error !== null || response.StatusCode !== 200) {
            const code = lifecycleErrorCode(response.Error);
            openNotification('Versiones', lifecycleErrorMessage(response.StatusCode, code));
            setVersions([]);
            return;
        }
        const body = response.Response as GeiInventoryVersion[];
        setVersions(Array.isArray(body) ? sortVersionsByCreatedAtDesc(body) : []);
    };

    useEffect(() => {
        if (props.open && props.geiInventoryId) {
            load(props.geiInventoryId);
        }
        if (!props.open) {
            setVersions([]);
        }
    }, [props.open, props.geiInventoryId]);

    const refresh = async () => {
        if (props.geiInventoryId) {
            await load(props.geiInventoryId);
        }
    };

    const handleMutation = async (request: Promise<{ StatusCode: number; Error: unknown; Response: unknown }>) => {
        setBusy(true);
        const response = await request;
        setBusy(false);
        if (response.Error !== null || (response.StatusCode !== 200 && response.StatusCode !== 201)) {
            const code = lifecycleErrorCode(response.Error);
            if (code === 'CORRECTION_REASON_REQUIRED') {
                setReasonError(lifecycleErrorMessage(response.StatusCode, code));
            } else {
                openNotification('Versiones', lifecycleErrorMessage(response.StatusCode, code));
            }
            if (lifecycleErrorShouldRefresh(response.StatusCode, code)) {
                await refresh();
            }
            return false;
        }
        await refresh();
        return true;
    };

    const confirmClose = async () => {
        if (!closeTarget) return;
        const ok = await handleMutation(postCloseGeiVersionAPI(closeTarget.id));
        if (ok) {
            setCloseTarget(null);
            openNotification('Versiones', 'La versión quedó cerrada y en solo lectura.');
        }
    };

    const confirmNext = async () => {
        if (!nextTarget) return;
        const ok = await handleMutation(postCreateNextGeiVersionAPI(nextTarget.id, nextLabel.trim() || null));
        if (ok) {
            setNextTarget(null);
            openNotification('Versiones', 'Se creó la siguiente versión abierta.');
        }
    };

    const confirmCorrective = async () => {
        if (!correctiveTarget) return;
        if (correctionReason.trim().length === 0) {
            setReasonError('Indique el motivo de la corrección.');
            return;
        }
        const ok = await handleMutation(postCreateCorrectiveGeiVersionAPI(
            correctiveTarget.id,
            correctionReason.trim(),
            correctiveLabel.trim() || null
        ));
        if (ok) {
            setCorrectiveTarget(null);
            setCorrectionReason('');
            setReasonError('');
            openNotification('Versiones', 'Se creó una versión correctiva abierta. La versión de origen no cambió.');
        }
    };

    const openNext = (version: GeiInventoryVersion) => {
        const nextNumber = Math.max(...versions.map((item) => item.versionNumber)) + 1;
        setNextLabel(suggestedNextLabel(nextNumber));
        setNextTarget(version);
    };

    const openCorrective = (version: GeiInventoryVersion) => {
        const nextNumber = Math.max(...versions.map((item) => item.versionNumber)) + 1;
        setCorrectiveLabel(suggestedNextLabel(nextNumber));
        setCorrectionReason('');
        setReasonError('');
        setCorrectiveTarget(version);
    };

    const nextNumber = versions.length === 0 ? 1 : Math.max(...versions.map((item) => item.versionNumber)) + 1;

    return (
        <>
            <Drawer
                title="Versiones"
                open={props.open}
                onClose={props.onClose}
                width={420}
            >
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Button onClick={() => props.onViewVersion(null)}>Ver versión actual</Button>
                    <List
                        loading={loading}
                        dataSource={versions}
                        locale={{ emptyText: 'Sin versiones' }}
                        renderItem={(version) => {
                            const actions = lifecycleActionVisibility(canManage, version.state, openExists);
                            const predecessor = versions.find((item) => item.id === version.predecessorVersionId);
                            return (
                                <List.Item>
                                    <Space direction="vertical" style={{ width: '100%' }} size={4}>
                                        <Space wrap>
                                            <Text strong>{formatVersionLabel(version.versionNumber, version.label)}</Text>
                                            <Tag color={version.state === 'OPEN' ? 'green' : 'default'}>{formatVersionStateBadge(version.state)}</Tag>
                                            {version.isCurrent ? <Tag color="blue">Actual</Tag> : null}
                                        </Space>
                                        <Text type="secondary">{formatVersionEventLine('Creada', formatWhen(version.createdAt), version.createdByName)}</Text>
                                        {version.closedAt ? <Text type="secondary">{formatVersionEventLine('Cerrada', formatWhen(version.closedAt), version.closedByName)}</Text> : null}
                                        {version.correctionReason ? <Text>Corrección: {version.correctionReason}</Text> : null}
                                        {predecessor ? <Text type="secondary">Sucesora de {formatVersionLabel(predecessor.versionNumber, predecessor.label)}</Text> : null}
                                        <Space wrap>
                                            <Button size="small" onClick={() => props.onViewVersion(version.id)}>Ver versión</Button>
                                            {actions.showClose ? <Button size="small" onClick={() => setCloseTarget(version)}>Cerrar versión</Button> : null}
                                            {actions.showCreateNext ? <Button size="small" onClick={() => openNext(version)}>Crear siguiente versión</Button> : null}
                                            {actions.showCreateCorrection ? <Button size="small" onClick={() => openCorrective(version)}>Crear corrección</Button> : null}
                                        </Space>
                                        {actions.creationBlockedReason ? <Text type="secondary">{actions.creationBlockedReason}</Text> : null}
                                    </Space>
                                </List.Item>
                            );
                        }}
                    />
                </Space>
            </Drawer>

            <Modal
                title="Cerrar versión"
                open={closeTarget !== null}
                onCancel={() => { if (!busy) setCloseTarget(null); }}
                onOk={confirmClose}
                okText="Cerrar versión"
                confirmLoading={busy}
                okButtonProps={{ disabled: busy }}
            >
                <p>Esta versión pasará a solo lectura.</p>
                <p>Los datos existentes se conservan.</p>
                <p>Los cambios posteriores requieren crear una nueva versión.</p>
            </Modal>

            <Modal
                title="Crear siguiente versión"
                open={nextTarget !== null}
                onCancel={() => { if (!busy) setNextTarget(null); }}
                onOk={confirmNext}
                okText="Crear"
                confirmLoading={busy}
                okButtonProps={{ disabled: busy }}
            >
                {nextTarget ? (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Text>Origen: {formatVersionLabel(nextTarget.versionNumber, nextTarget.label)}</Text>
                        <Text>Nueva versión: {nextNumber}</Text>
                        <Form layout="vertical">
                            <Form.Item label="Etiqueta (opcional)">
                                <Input value={nextLabel} onChange={(event) => setNextLabel(event.target.value)} />
                            </Form.Item>
                        </Form>
                    </Space>
                ) : null}
            </Modal>

            <Modal
                title="Crear corrección"
                open={correctiveTarget !== null}
                onCancel={() => { if (!busy) setCorrectiveTarget(null); }}
                onOk={confirmCorrective}
                okText="Crear corrección"
                confirmLoading={busy}
                okButtonProps={{ disabled: busy }}
            >
                {correctiveTarget ? (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Text>Origen cerrado: {formatVersionLabel(correctiveTarget.versionNumber, correctiveTarget.label)}</Text>
                        <Text>Nueva versión: {nextNumber}</Text>
                        <Alert
                            type={correctiveTarget.label === 'Final' ? 'warning' : 'info'}
                            showIcon
                            message="La versión de origen no se modifica. Se crea una nueva versión abierta. Los datos históricos no se sobrescriben."
                        />
                        <Form layout="vertical">
                            <Form.Item label="Etiqueta (opcional)">
                                <Input value={correctiveLabel} onChange={(event) => setCorrectiveLabel(event.target.value)} />
                            </Form.Item>
                            <Form.Item
                                label="Motivo de la corrección"
                                validateStatus={reasonError ? 'error' : ''}
                                help={reasonError || undefined}
                                required
                            >
                                <Input.TextArea
                                    rows={3}
                                    value={correctionReason}
                                    onChange={(event) => {
                                        setCorrectionReason(event.target.value);
                                        if (event.target.value.trim().length > 0) setReasonError('');
                                    }}
                                />
                            </Form.Item>
                        </Form>
                    </Space>
                ) : null}
            </Modal>
        </>
    );
};

export default VersionHistoryDrawer;
